import {
  Autocomplete,
  Box,
  Chip,
  Collapse,
  TextField,
  Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import Checkbox from '@components/Checkbox';
import {
  FormikCheckboxField,
  FormikDropdownField,
  FormikTextField,
} from '@components/FormikFields';
import Icon from '@components/Icon/Icon';
import InputLabel from '@components/InputLabel';

import {
  FREQUENCY_PERIOD_UNITS,
  FREQUENCY_PRESETS,
  RATE_UNITS,
  TIMING_OPTIONS,
  unitOptions,
} from '@constants/medication-dosage';

import { ColumnLayout } from '@layouts/DrilldownStyle';

import {
  type DosageStep,
  stepsToDosage,
  summarizeForList,
  summarizeStep,
} from '@utils/fhir-mappers/dosageSteps';
import {
  type MedicationIngredientRow,
  formatStrengths,
} from '@utils/fhir-mappers/medicationIngredients';

import {
  DisclosureButton,
  GatedBlock,
  OptionalTag,
  PreviewBox,
  PreviewText,
  StepBox,
} from './DosageSteps.style';
import {
  DynamicNoun,
  FieldCell,
  FieldRow,
  RangeButton,
  RowHelp,
  SectionBlock,
  SectionBody,
  SectionError,
  SectionHeader,
  SectionHint,
  SectionTitle,
  SentenceCell,
  SentenceErrors,
  SentenceRow,
} from './Fields.style';
import { useDoseFormNoun } from './useDoseFormNoun';
import { useSentenceErrors } from './useSentenceErrors';

const messages = defineMessages({
  dosage: { id: 'medicationDosage.dosage', defaultMessage: 'Dosage' },
  /**
   * The dose itself moved into the "how much medicine" block, so what is left
   * here is the schedule. Read mode still says "Dosage": it prints the whole
   * sentence, dose included.
   */
  schedule: { id: 'medicationDosage.schedule', defaultMessage: 'How often' },
  step: { id: 'medicationDosage.step', defaultMessage: 'Step {n}' },
  frequency: { id: 'medicationDosage.frequency', defaultMessage: 'Frequency' },
  /**
   * The custom schedule as one sentence: "Take 3 times every 4 hours". Was four
   * boxes labelled Times / Every / Interval to / Unit, which named the fields
   * after the FHIR members rather than after anything a curator says out loud.
   */
  frequencySentence: {
    id: 'medicationDosage.frequencySentence',
    defaultMessage: 'Take {times}{timesNoun} every {every}{unit}',
  },
  frequencySentenceRange: {
    id: 'medicationDosage.frequencySentenceRange',
    defaultMessage: 'Take {times}{timesNoun} every {every} to {everyMax}{unit}',
  },
  /** Singular and plural of the count, so "1 time" doesn't read as "1 times". */
  timesNoun: { id: 'medicationDosage.timesNoun', defaultMessage: 'time' },
  timesNounPlural: {
    id: 'medicationDosage.timesNounPlural',
    defaultMessage: 'times',
  },
  removeInterval: {
    id: 'medicationDosage.removeInterval',
    defaultMessage: 'remove range',
  },
  removeTotalDoses: {
    id: 'medicationDosage.removeTotalDoses',
    defaultMessage: 'remove total',
  },
  addInterval: {
    id: 'medicationDosage.addInterval',
    defaultMessage: 'or a range',
  },
  times: { id: 'medicationDosage.times', defaultMessage: 'Times' },
  every: { id: 'medicationDosage.every', defaultMessage: 'Every' },
  everyMax: {
    id: 'medicationDosage.everyMax',
    defaultMessage: 'Interval to',
  },
  frequencyUnit: {
    id: 'medicationDosage.frequencyUnit',
    defaultMessage: 'Interval unit',
  },
  frequencyCount: {
    id: 'medicationDosage.frequencyCount',
    defaultMessage: 'Total doses',
  },
  /** The rate as one sentence: "At 100 mL per 1 hour". */
  rateSentence: {
    id: 'medicationDosage.rateSentence',
    defaultMessage: 'At {amount}{unit} per {periodValue}{periodUnit}',
  },
  rateValue: {
    id: 'medicationDosage.rateValue',
    defaultMessage: 'Rate',
  },
  rateUnit: { id: 'medicationDosage.rateUnit', defaultMessage: 'Rate unit' },
  ratePerValue: {
    id: 'medicationDosage.ratePerValue',
    defaultMessage: 'Rate period amount',
  },
  ratePerUnit: {
    id: 'medicationDosage.ratePerUnit',
    defaultMessage: 'Rate period',
  },
  asNeeded: {
    id: 'medicationDosage.asNeeded',
    defaultMessage: 'As needed (PRN)',
  },
  timing: {
    id: 'medicationDosage.timing',
    defaultMessage: 'Timing (optional)',
  },
  /** The course as one sentence: "For 10 days, 30 doses in total". */
  courseSentence: {
    id: 'medicationDosage.courseSentence',
    defaultMessage: 'For {duration}{unit}',
  },
  courseSentenceTotal: {
    id: 'medicationDosage.courseSentenceTotal',
    defaultMessage: 'For {duration}{unit}, {count}{countNoun} in total',
  },
  doseNoun: { id: 'medicationDosage.doseNoun', defaultMessage: 'dose' },
  doseNounPlural: {
    id: 'medicationDosage.doseNounPlural',
    defaultMessage: 'doses',
  },
  addTotalDoses: {
    id: 'medicationDosage.addTotalDoses',
    defaultMessage: 'or a total number of doses',
  },
  durationValue: {
    id: 'medicationDosage.durationValue',
    defaultMessage: 'Duration',
  },
  durationUnit: {
    id: 'medicationDosage.durationUnit',
    defaultMessage: 'Duration unit',
  },
  maxDoseValue: {
    id: 'medicationDosage.maxDoseValue',
    defaultMessage: 'Max per period',
  },
  /** The ceiling as one sentence: "Max 8 tablets per 1 day". */
  maxDoseSentence: {
    id: 'medicationDosage.maxDoseSentence',
    defaultMessage: 'Max {amount}{form} per {periodValue}{periodUnit}',
  },
  maxDosePeriodValue: {
    id: 'medicationDosage.maxDosePeriodValue',
    defaultMessage: 'Period amount',
  },
  maxDosePeriodUnit: {
    id: 'medicationDosage.maxDosePeriodUnit',
    defaultMessage: 'Period',
  },
  asDirected: {
    id: 'medicationDosage.asDirected',
    defaultMessage: 'As directed',
  },
  preview: {
    id: 'medicationDosage.preview',
    defaultMessage: 'Shows in the list as',
  },
  previewEmpty: {
    id: 'medicationDosage.previewEmpty',
    defaultMessage: 'Nothing yet. Enter a dose, frequency, or instruction.',
  },
  none: { id: 'medicationDosage.none', defaultMessage: '—' },
  sectionHelp: {
    id: 'medicationDosage.sectionHelp',
    defaultMessage:
      'How often the patient takes it. e.g. twice daily with meals',
  },
  asNeededHelp: {
    id: 'medicationDosage.asNeededHelp',
    defaultMessage:
      'Adds to the schedule above rather than replacing it. Tick both for "twice daily as needed".',
  },
  maxDoseHelp: {
    id: 'medicationDosage.maxDoseHelp',
    defaultMessage:
      'A limit not to exceed, separate from the dose. e.g. max 8 tablets per day',
  },
  frequencyCountHelp: {
    id: 'medicationDosage.frequencyCountHelp',
    defaultMessage:
      'Total for the whole course, not per day. e.g. 3 doses in total',
  },
  rateHelp: {
    id: 'medicationDosage.rateHelp',
    defaultMessage: 'For infusions. e.g. 100 mL per hour',
  },
  asDirectedHelp: {
    id: 'medicationDosage.asDirectedHelp',
    defaultMessage:
      'Kept for records that already use it. Put new notes in Instructions instead.',
  },
  courseGate: {
    id: 'medicationDosage.courseGate',
    defaultMessage: 'Course has a fixed length',
  },
  infusionGate: {
    id: 'medicationDosage.infusionGate',
    defaultMessage: 'Given as an infusion',
  },
  moreSection: {
    id: 'medicationDosage.moreSection',
    defaultMessage: 'Timing of day, dose limits',
  },
  optional: { id: 'medicationDosage.optional', defaultMessage: 'optional' },
});

const blankOption = (label: string) => ({ k: '', v: label });

/** Period units a "max per period" ceiling can be expressed against. */
const MAX_DOSE_PERIOD_UNITS = FREQUENCY_PERIOD_UNITS;

interface DosageStepsProps {
  isEditing: boolean;
}

/**
 * Single-dosage editor. Editing is limited to one `Dosage` object since no
 * medicationstatement on production needs multi-dosage/titration. The read
 * view still renders every step of any legacy multi-dosage record. Maps
 * to/from a FHIR `dosage[]` via the dosageSteps mappers.
 */
export default function DosageSteps({ isEditing }: DosageStepsProps) {
  const { formatMessage } = useIntl();
  const doseFormNoun = useDoseFormNoun();
  const maxDoseErrors = useSentenceErrors([
    'dosageSteps.0.maxDoseValue',
    'dosageSteps.0.maxDosePeriodValue',
  ]);
  const frequencyErrors = useSentenceErrors([
    'dosageSteps.0.frequencyTimes',
    'dosageSteps.0.frequencyEvery',
    'dosageSteps.0.frequencyEveryMax',
    'dosageSteps.0.frequencyUnit',
  ]);
  const courseErrors = useSentenceErrors([
    'dosageSteps.0.durationValue',
    'dosageSteps.0.durationUnit',
    'dosageSteps.0.frequencyCount',
  ]);
  const rateErrors = useSentenceErrors([
    'dosageSteps.0.rateValue',
    'dosageSteps.0.rateUnit',
    'dosageSteps.0.ratePerValue',
    'dosageSteps.0.ratePerUnit',
  ]);
  const { values, setFieldValue, errors, submitCount } = useFormikContext<{
    dosageSteps: DosageStep[];
    ingredients?: MedicationIngredientRow[];
    doseForm?: string;
    route?: string;
    instructions?: string;
  }>();
  const steps = values.dosageSteps;

  /**
   * Whether each advanced block is showing. `null` means "follow the record", so
   * a stored duration or rate reveals its own block — including when the record
   * loads after this mounts, which it does. Once the curator clicks, their
   * choice wins.
   */
  const [courseOpen, setCourseOpen] = useState<boolean | null>(null);
  const [infusionOpen, setInfusionOpen] = useState<boolean | null>(null);
  const [extrasOpen, setExtrasOpen] = useState<boolean | null>(null);

  /**
   * The second half of the two ranges in this section — the interval's upper
   * bound and the course's total dose count — is opt-in, the same way the dose
   * range is. Most schedules are a single interval and most courses have no
   * separate total, and an always-present empty box after "to" reads as a field
   * someone forgot rather than one nobody needed. A record that already holds
   * the value shows it without being asked.
   */
  const [openInterval, setOpenInterval] = useState(false);
  const [openTotalDoses, setOpenTotalDoses] = useState(false);

  /**
   * "As directed" is no longer offered for new records: curators read the old
   * "As directed (free text)" label as a second Instructions box and filled in
   * whichever they hit first. It stays visible on the records that already carry
   * one so their text can still be read and edited.
   *
   * Latched rather than derived. The record loads after this mounts, so the
   * field has to appear when the value arrives — but once shown it stays, or
   * clearing the text would unmount the input the curator is typing in.
   */
  const asDirectedText = steps[0]?.asDirected ?? '';
  const [showAsDirected, setShowAsDirected] = useState(false);
  useEffect(() => {
    if (asDirectedText) setShowAsDirected(true);
  }, [asDirectedText]);

  if (!isEditing) {
    const lines = steps.map((s) => summarizeStep(s)).filter(Boolean);
    return (
      <Box sx={{ width: '100%', mb: '10px' }}>
        <InputLabel label={formatMessage(messages.dosage)} />
        {lines.length ? (
          lines.map((line, i) => (
            <Typography
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              data-testid="dosage-step-summary"
              sx={{ fontSize: '16px', wordBreak: 'break-word' }}
            >
              {steps.length > 1
                ? `${formatMessage(messages.step, { n: i + 1 })}: ${line}`
                : line}
            </Typography>
          ))
        ) : (
          <Typography sx={{ fontSize: '16px' }}>-</Typography>
        )}
      </Box>
    );
  }

  const none = formatMessage(messages.none);
  const step = steps[0];
  /**
   * "max 8 tablets per day" — pluralised off the ceiling, not off the dose.
   *
   * Reads the ceiling's own stored unit when it has one. Taking the noun
   * straight from Form printed "max 30 tablets" over a payload that still said
   * `30 mL`, so the screen named a unit the record didn't hold and named the
   * real one nowhere. Picking a Form rewrites the stored unit, and then the two
   * agree again.
   */
  const maxDoseNoun = doseFormNoun(
    step.maxDoseUnit || values.doseForm || '',
    Number(step.maxDoseValue || 1)
  );
  // The same line the medications list prints, built the same way: strength,
  // then the structured dosage without the prose. Instructions are left out of
  // both — a sentence of them crowds out the dose.
  const preview = summarizeForList(
    stepsToDosage([step], { route: values.route || undefined })[0],
    formatStrengths(values.ingredients)
  );

  // Yup attaches an array-level test's message to `errors.dosageSteps` as a
  // string rather than to any field, so it has to be rendered here.
  const stepsError =
    typeof errors.dosageSteps === 'string' ? errors.dosageSteps : '';

  // A number input makes Formik coerce the value to a JS number, but DosageStep
  // fields are strings (matching how dosageToSteps hydrates them and what the
  // mappers expect). Write the raw string back so typed and hydrated values
  // share one representation.
  const keepString = (name: string) => (value: number | string) =>
    setFieldValue(name, String(value));

  const COURSE_FIELDS = ['durationValue', 'durationUnit', 'frequencyCount'];
  const INFUSION_FIELDS = [
    'rateValue',
    'rateUnit',
    'ratePerValue',
    'ratePerUnit',
  ];
  // `maxDoseUnit` is deliberately absent: it is written from Form now, so a
  // record with nothing but a unit is not a record with a ceiling, and keying
  // the disclosure on it would pop this section open the moment a Form is set.
  const EXTRAS_FIELDS = [
    'maxDoseValue',
    'maxDosePeriodValue',
    'maxDosePeriodUnit',
  ];

  const hasAny = (names: string[]) =>
    names.some((name) => Boolean(step[name as keyof DosageStep]));

  const showCourse = courseOpen ?? hasAny(COURSE_FIELDS);
  const showInfusion = infusionOpen ?? hasAny(INFUSION_FIELDS);
  const showInterval = openInterval || Boolean(step.frequencyEveryMax);
  const showTotalDoses = openTotalDoses || Boolean(step.frequencyCount);

  /**
   * Closing one of these opt-in halves has to clear its field as well as hide
   * it. Both `show` flags read the stored value too, so a value left behind
   * reopens the input on the next render — and one nobody can see would still
   * be saved.
   */
  const closeOptional = (open: (v: boolean) => void, field: string) => () => {
    open(false);
    setFieldValue(`dosageSteps.0.${field}`, '');
  };

  /**
   * "Take 1 time every 4 hours", not "1 times". The count is a React node in the
   * sentence, so ICU can't pluralise off it and the word is picked here — the
   * same way the dose form noun is.
   */
  const timesNoun = formatMessage(
    Number(step.frequencyTimes) === 1
      ? messages.timesNoun
      : messages.timesNounPlural
  );
  const doseCountNoun = formatMessage(
    Number(step.frequencyCount) === 1
      ? messages.doseNoun
      : messages.doseNounPlural
  );
  const showExtras =
    extrasOpen ?? (step.timing.length > 0 || hasAny(EXTRAS_FIELDS));

  /**
   * Unticking a gate clears its fields. Hiding them while they still hold values
   * would save a rate or a course length the curator has just said doesn't
   * apply, with nothing on screen to show it happened.
   */
  const closeGate = (names: string[]) =>
    names.forEach((name) => setFieldValue(`dosageSteps.0.${name}`, ''));

  /**
   * Closing hands control back to the record (`null`) rather than pinning the
   * gate shut. Since closing also clears the fields, the derived state is
   * already false, so it looks the same — but if the record is later
   * reinitialised with values in this block (Formik has `enableReinitialize`,
   * and a refetch resets the form), the block reopens instead of holding those
   * values off-screen.
   */
  const setGate = (
    set: (value: boolean | null) => void,
    fields: string[],
    checked: boolean
  ) => {
    if (checked) {
      set(true);
      return;
    }
    set(null);
    closeGate(fields);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Boxed the same way as "How much": the two are the questions a curator
          is answering, and only one of them looking like a section made the
          schedule read as an appendix to the dose. */}
      <SectionBlock data-testid="dosage-section">
        <SectionHeader>
          <SectionTitle>{formatMessage(messages.schedule)}</SectionTitle>
          <SectionHint data-testid="dosage-help">
            {formatMessage(messages.sectionHelp)}
          </SectionHint>
        </SectionHeader>
        <SectionBody>
          {/* The patient-self-report rule attaches its message to the
              dosageSteps array itself, so nothing rendered it: Save appeared
              to do nothing. */}
          {stepsError && submitCount > 0 && (
            <SectionError role="alert" data-testid="dosage-steps-error">
              {stepsError}
            </SectionError>
          )}

          <StepBox data-testid="dosage-step-row">
            <FieldRow>
              <FieldCell $size="text">
                <FormikDropdownField
                  label={formatMessage(messages.frequency)}
                  name="dosageSteps.0.frequencyPreset"
                  options={[
                    blankOption(none),
                    ...FREQUENCY_PRESETS.map((p) => ({
                      k: p.value,
                      v: p.label,
                    })),
                  ]}
                />
              </FieldCell>
            </FieldRow>

            {/* Revealed by the Frequency dropdown above, so it gets the same
                left rule as the other gated blocks: without it the sentence
                reads as another top-level row of the form. */}
            {step.frequencyPreset === 'custom' && (
              <GatedBlock data-testid="frequency-custom-block">
                <SentenceRow data-testid="frequency-custom-row">
                  <FormattedMessage
                    {...(showInterval
                      ? messages.frequencySentenceRange
                      : messages.frequencySentence)}
                    values={{
                      timesNoun,
                      times: (
                        <SentenceCell $size="number" key="times">
                          <FormikTextField
                            type="number"
                            label={formatMessage(messages.times)}
                            name="dosageSteps.0.frequencyTimes"
                            onChange={keepString(
                              'dosageSteps.0.frequencyTimes'
                            )}
                          />
                        </SentenceCell>
                      ),
                      every: (
                        <SentenceCell $size="number" key="every">
                          <FormikTextField
                            type="number"
                            label={formatMessage(messages.every)}
                            name="dosageSteps.0.frequencyEvery"
                            onChange={keepString(
                              'dosageSteps.0.frequencyEvery'
                            )}
                          />
                        </SentenceCell>
                      ),
                      everyMax: showInterval ? (
                        <SentenceCell $size="number" key="everyMax">
                          <FormikTextField
                            type="number"
                            label={formatMessage(messages.everyMax)}
                            name="dosageSteps.0.frequencyEveryMax"
                            onChange={keepString(
                              'dosageSteps.0.frequencyEveryMax'
                            )}
                          />
                        </SentenceCell>
                      ) : null,
                      unit: (
                        <SentenceCell $size="unit" key="unit">
                          <FormikDropdownField
                            ariaLabel={formatMessage(messages.frequencyUnit)}
                            name="dosageSteps.0.frequencyUnit"
                            options={[
                              blankOption(none),
                              ...FREQUENCY_PERIOD_UNITS.map((u) => ({
                                k: u.value,
                                v: u.label,
                              })),
                            ]}
                          />
                        </SentenceCell>
                      ),
                    }}
                  />
                  {showInterval ? (
                    <RangeButton
                      data-testid="frequency-remove-range"
                      onClick={closeOptional(
                        setOpenInterval,
                        'frequencyEveryMax'
                      )}
                      type="button"
                    >
                      {formatMessage(messages.removeInterval)}
                    </RangeButton>
                  ) : (
                    <RangeButton
                      data-testid="frequency-add-range"
                      onClick={() => setOpenInterval(true)}
                      type="button"
                    >
                      {formatMessage(messages.addInterval)}
                    </RangeButton>
                  )}
                </SentenceRow>
                {frequencyErrors.length > 0 && (
                  <SentenceErrors
                    role="alert"
                    data-testid="frequency-custom-errors"
                  >
                    {frequencyErrors.join(' ')}
                  </SentenceErrors>
                )}
              </GatedBlock>
            )}

            {/* PRN is additive to the schedule above, never a replacement for it. */}
            <ColumnLayout>
              <FormikCheckboxField
                name="dosageSteps.0.asNeeded"
                label={formatMessage(messages.asNeeded)}
                helperText={formatMessage(messages.asNeededHelp)}
              />
            </ColumnLayout>

            {/* Course length. Gated: most records are open-ended. */}
            <ColumnLayout>
              <Checkbox
                checked={showCourse}
                label={formatMessage(messages.courseGate)}
                name="dosage-course-gate"
                onChange={(event) =>
                  setGate(setCourseOpen, COURSE_FIELDS, event.target.checked)
                }
              />
            </ColumnLayout>
            {showCourse && (
              <GatedBlock data-testid="dosage-course-block">
                <SentenceRow data-testid="dosage-course-row">
                  <FormattedMessage
                    {...(showTotalDoses
                      ? messages.courseSentenceTotal
                      : messages.courseSentence)}
                    values={{
                      countNoun: doseCountNoun,
                      duration: (
                        <SentenceCell $size="number" key="duration">
                          <FormikTextField
                            type="number"
                            label={formatMessage(messages.durationValue)}
                            name="dosageSteps.0.durationValue"
                            onChange={keepString('dosageSteps.0.durationValue')}
                          />
                        </SentenceCell>
                      ),
                      unit: (
                        <SentenceCell $size="unit" key="unit">
                          <FormikDropdownField
                            ariaLabel={formatMessage(messages.durationUnit)}
                            name="dosageSteps.0.durationUnit"
                            options={[
                              blankOption(none),
                              ...FREQUENCY_PERIOD_UNITS.map((u) => ({
                                k: u.value,
                                v: u.label,
                              })),
                            ]}
                          />
                        </SentenceCell>
                      ),
                      // Total administrations over the course, not per day.
                      count: showTotalDoses ? (
                        <SentenceCell $size="number" key="count">
                          <FormikTextField
                            type="number"
                            label={formatMessage(messages.frequencyCount)}
                            name="dosageSteps.0.frequencyCount"
                            onChange={keepString(
                              'dosageSteps.0.frequencyCount'
                            )}
                          />
                        </SentenceCell>
                      ) : null,
                    }}
                  />
                  {showTotalDoses ? (
                    <RangeButton
                      data-testid="course-remove-total"
                      onClick={closeOptional(
                        setOpenTotalDoses,
                        'frequencyCount'
                      )}
                      type="button"
                    >
                      {formatMessage(messages.removeTotalDoses)}
                    </RangeButton>
                  ) : (
                    <RangeButton
                      data-testid="course-add-total"
                      onClick={() => setOpenTotalDoses(true)}
                      type="button"
                    >
                      {formatMessage(messages.addTotalDoses)}
                    </RangeButton>
                  )}
                </SentenceRow>
                {courseErrors.length > 0 && (
                  <SentenceErrors
                    role="alert"
                    data-testid="dosage-course-errors"
                  >
                    {courseErrors.join(' ')}
                  </SentenceErrors>
                )}
                {showTotalDoses && (
                  <RowHelp>
                    {formatMessage(messages.frequencyCountHelp)}
                  </RowHelp>
                )}
              </GatedBlock>
            )}

            {/* Administration rate, e.g. an infusion at 100 mL per 1 hour. */}
            <ColumnLayout>
              <Checkbox
                checked={showInfusion}
                label={formatMessage(messages.infusionGate)}
                name="dosage-infusion-gate"
                onChange={(event) =>
                  setGate(
                    setInfusionOpen,
                    INFUSION_FIELDS,
                    event.target.checked
                  )
                }
              />
            </ColumnLayout>
            {showInfusion && (
              <GatedBlock data-testid="dosage-infusion-block">
                <SentenceRow data-testid="dosage-infusion-row">
                  <FormattedMessage
                    {...messages.rateSentence}
                    values={{
                      amount: (
                        <SentenceCell $size="number" key="amount">
                          <FormikTextField
                            type="number"
                            label={formatMessage(messages.rateValue)}
                            name="dosageSteps.0.rateValue"
                            onChange={keepString('dosageSteps.0.rateValue')}
                          />
                        </SentenceCell>
                      ),
                      // A rate is always measured ("100 mL per hour"), never
                      // counted.
                      unit: (
                        <SentenceCell $size="unit" key="unit">
                          <FormikDropdownField
                            ariaLabel={formatMessage(messages.rateUnit)}
                            name="dosageSteps.0.rateUnit"
                            options={[
                              blankOption(none),
                              ...unitOptions(RATE_UNITS, step.rateUnit),
                            ]}
                          />
                        </SentenceCell>
                      ),
                      // Blank means per one, which is how a bare rate reads.
                      periodValue: (
                        <SentenceCell $size="number" key="periodValue">
                          <FormikTextField
                            type="number"
                            label={formatMessage(messages.ratePerValue)}
                            name="dosageSteps.0.ratePerValue"
                            onChange={keepString('dosageSteps.0.ratePerValue')}
                          />
                        </SentenceCell>
                      ),
                      periodUnit: (
                        <SentenceCell $size="unit" key="periodUnit">
                          <FormikDropdownField
                            ariaLabel={formatMessage(messages.ratePerUnit)}
                            name="dosageSteps.0.ratePerUnit"
                            options={[
                              blankOption(none),
                              ...FREQUENCY_PERIOD_UNITS.map((u) => ({
                                k: u.value,
                                v: u.label,
                              })),
                            ]}
                          />
                        </SentenceCell>
                      ),
                    }}
                  />
                </SentenceRow>
                {rateErrors.length > 0 && (
                  <SentenceErrors
                    role="alert"
                    data-testid="dosage-infusion-errors"
                  >
                    {rateErrors.join(' ')}
                  </SentenceErrors>
                )}
                <RowHelp>{formatMessage(messages.rateHelp)}</RowHelp>
              </GatedBlock>
            )}

            {/* Time of day and the dose ceiling: real but rarely specified, so
            collapsed rather than checkbox-gated — opening them asserts nothing. */}
            <DisclosureButton
              aria-expanded={showExtras}
              data-testid="dosage-extras-toggle"
              onClick={() => setExtrasOpen(!showExtras)}
              type="button"
            >
              <Icon
                name={showExtras ? 'chevron-down' : 'chevron-right'}
                size="xs"
                type="fas"
              />
              {formatMessage(messages.moreSection)}
              <OptionalTag>{formatMessage(messages.optional)}</OptionalTag>
            </DisclosureButton>
            <Collapse in={showExtras} unmountOnExit>
              <GatedBlock data-testid="dosage-extras-block">
                <ColumnLayout>
                  <Autocomplete
                    multiple
                    options={TIMING_OPTIONS.map((o) => o.value)}
                    getOptionLabel={(option) =>
                      TIMING_OPTIONS.find((o) => o.value === option)?.label ??
                      option
                    }
                    value={step.timing}
                    onChange={(_event, newValue) => {
                      setFieldValue('dosageSteps.0.timing', newValue);
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={
                            TIMING_OPTIONS.find((o) => o.value === option)
                              ?.label ?? option
                          }
                          {...getTagProps({ index })}
                          key={option}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={formatMessage(messages.timing)}
                        variant="outlined"
                        size="small"
                      />
                    )}
                  />
                </ColumnLayout>

                {/* A ceiling caps the dose, so it counts the same thing the
                    dose does — the noun comes from Form, not a dropdown of its
                    own. The period is a length of time, so that stays a pick. */}
                <SentenceRow data-testid="max-dose-row">
                  <FormattedMessage
                    {...messages.maxDoseSentence}
                    values={{
                      form: <DynamicNoun key="form">{maxDoseNoun}</DynamicNoun>,
                      amount: (
                        <SentenceCell $size="number" key="amount">
                          <FormikTextField
                            type="number"
                            label={formatMessage(messages.maxDoseValue)}
                            name="dosageSteps.0.maxDoseValue"
                            onChange={keepString('dosageSteps.0.maxDoseValue')}
                          />
                        </SentenceCell>
                      ),
                      periodValue: (
                        <SentenceCell $size="number" key="periodValue">
                          <FormikTextField
                            type="number"
                            label={formatMessage(messages.maxDosePeriodValue)}
                            name="dosageSteps.0.maxDosePeriodValue"
                            onChange={keepString(
                              'dosageSteps.0.maxDosePeriodValue'
                            )}
                          />
                        </SentenceCell>
                      ),
                      periodUnit: (
                        <SentenceCell $size="unit" key="periodUnit">
                          <FormikDropdownField
                            ariaLabel={formatMessage(
                              messages.maxDosePeriodUnit
                            )}
                            name="dosageSteps.0.maxDosePeriodUnit"
                            options={[
                              blankOption(none),
                              ...MAX_DOSE_PERIOD_UNITS.map((u) => ({
                                k: u.value,
                                v: u.label,
                              })),
                            ]}
                          />
                        </SentenceCell>
                      ),
                    }}
                  />
                </SentenceRow>
                {maxDoseErrors.length > 0 && (
                  <SentenceErrors role="alert" data-testid="max-dose-errors">
                    {maxDoseErrors.join(' ')}
                  </SentenceErrors>
                )}
                <RowHelp>{formatMessage(messages.maxDoseHelp)}</RowHelp>
              </GatedBlock>
            </Collapse>

            {showAsDirected && (
              <ColumnLayout data-testid="dosage-as-directed">
                <FormikTextField
                  fullWidth
                  label={formatMessage(messages.asDirected)}
                  name="dosageSteps.0.asDirected"
                  helperText={formatMessage(messages.asDirectedHelp)}
                />
              </ColumnLayout>
            )}
          </StepBox>
        </SectionBody>
      </SectionBlock>

      <PreviewBox data-testid="dosage-preview">
        <InputLabel label={formatMessage(messages.preview)} />
        <PreviewText>
          {preview || formatMessage(messages.previewEmpty)}
        </PreviewText>
      </PreviewBox>
    </Box>
  );
}
